import { useId } from 'react';

interface RatingStarsProps {
  /** Rating value, e.g. 4.9. Clamped to [0, max]. */
  value: number;
  /** Total number of stars rendered. Defaults to 5. */
  max?: number;
  /** Accessible label, e.g. "4.9 out of 5 stars on the App Store". */
  label: string;
  /** Optional class name applied to the outer span. */
  className?: string;
}

const STAR_PATH =
  'M10 1.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8L10 14.7l-5.2 2.8 1-5.8L1.6 7.6l5.8-.8z';

const FILLED = '#F59E0B';
const EMPTY = 'rgba(245, 158, 11, 0.18)';

export function RatingStars({ value, max = 5, label, className }: RatingStarsProps) {
  const instanceId = useId();
  const clamped = Math.max(0, Math.min(max, value));

  return (
    <span
      role="img"
      aria-label={label}
      className={`inline-flex items-center gap-[2px] ${className ?? ''}`}
    >
      {Array.from({ length: max }, (_, i) => {
        const fillRatio = Math.max(0, Math.min(1, clamped - i));
        return (
          <Star key={i} fill={fillRatio} gradientId={`${instanceId}-${i}`} />
        );
      })}
    </span>
  );
}

interface StarProps {
  /** 0 = empty, 1 = full, 0..1 = partial fill from left. */
  fill: number;
  gradientId: string;
}

function Star({ fill, gradientId }: StarProps) {
  const isPartial = fill > 0 && fill < 1;
  const stop = `${(fill * 100).toFixed(2)}%`;

  return (
    <svg
      viewBox="0 0 20 20"
      className="h-3.5 w-3.5"
      aria-hidden="true"
      fill={isPartial ? `url(#${gradientId})` : fill === 1 ? FILLED : EMPTY}
    >
      {isPartial && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset={stop} stopColor={FILLED} />
            <stop offset={stop} stopColor={EMPTY} />
          </linearGradient>
        </defs>
      )}
      <path d={STAR_PATH} />
    </svg>
  );
}
